import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { KPICard } from '../components/KPICard';
import { DoctorWidget } from '../components/DoctorWidget';
import { AgendaDelDia } from '../components/AgendaDelDia';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { estadoDe } from '../lib/estados';
import { dinero, fechaLarga } from '../lib/identity';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Users,
  DollarSign,
  CalendarCheck,
  CalendarX,
  ArrowRight,
  Clock,
  AlertTriangle,
  DoorOpen,
  UserRoundSearch,
  Flame,
  CalendarDays,
  Plus,
  CheckCircle2,
} from 'lucide-react';

import { API } from '@/lib/api';

const DIA = 24 * 60 * 60 * 1000;
const iso = (d) => new Date(d).toISOString().split('T')[0];

/** Suma n días a una fecha ISO (yyyy-mm-dd) sin líos de zona horaria. */
const sumarDias = (isoFecha, n) => iso(new Date(`${isoFecha}T12:00:00`).getTime() + n * DIA);

/** Serie diaria de una métrica sobre los últimos 7 días (para el sparkline). */
const serieSemanal = (citas, dias, filtro) => dias.map((d) => citas.filter((c) => c.fecha === d && filtro(c)).length);

export const AdminDashboard = () => {
  const navigate = useNavigate();

  const [cargando, setCargando] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [doctores, setDoctores] = useState([]);
  const [citas, setCitas] = useState([]);
  const [pacientes, setPacientes] = useState({});
  const [pendientes, setPendientes] = useState({ sinConfirmar: 0, prospectosNuevos: null, reto21: null });
  const [seleccion, setSeleccion] = useState(null);

  const hoy = iso(new Date());

  const cargar = useCallback(async () => {
    const mesInicio = `${hoy.slice(0, 7)}-01`;
    const mesFin = sumarDias(hoy, 30);
    try {
      const [k, d, c, prospectos, reto] = await Promise.all([
        axios.get(`${API}/dashboard/kpis`),
        axios.get(`${API}/doctors`),
        axios.get(`${API}/appointments`, { params: { fecha_inicio: mesInicio, fecha_fin: mesFin, limit: 1000 } }),
        axios.get(`${API}/patients/count`, { params: { solo_prospectos: true, seguimiento: 'sin' } }).catch(() => null),
        axios.get(`${API}/patients/count`, { params: { en_reto21: true } }).catch(() => null),
      ]);

      setKpis(k.data);
      setDoctores(Array.isArray(d.data) ? d.data : []);
      const listaCitas = Array.isArray(c.data) ? c.data : [];
      setCitas(listaCitas);

      // Solo los pacientes de las citas ya cargadas, no los 500 más recientes
      // de la clínica (que además podían no incluir al paciente de una cita
      // vieja si ya hay más de 500 pacientes).
      const idsCitas = [...new Set(listaCitas.map((x) => x.paciente_id).filter(Boolean))];
      const p = idsCitas.length
        ? await axios.get(`${API}/patients`, { params: { ids: idsCitas.join(','), limit: idsCitas.length } })
        : { data: [] };
      setPacientes(Object.fromEntries((Array.isArray(p.data) ? p.data : []).map((x) => [x.id, x])));

      const proximos7 = Array.from({ length: 7 }, (_, i) => sumarDias(hoy, i));
      setPendientes({
        sinConfirmar: listaCitas.filter((x) => proximos7.includes(x.fecha) && x.estado === 'citado').length,
        prospectosNuevos: prospectos?.data?.total ?? null,
        reto21: reto?.data?.total ?? null,
      });
    } catch (err) {
      console.error('Error fetching dashboard data', err);
      toast.error('No se pudo cargar la información del día');
    } finally {
      setCargando(false);
    }
  }, [hoy]);

  useEffect(() => { cargar(); }, [cargar]);

  const citasHoy = useMemo(() => citas.filter((c) => c.fecha === hoy), [citas, hoy]);
  const ultimos7 = useMemo(() => Array.from({ length: 7 }, (_, i) => sumarDias(hoy, i - 6)), [hoy]);

  /**
   * Cambio de estado con UI optimista: la fila se mueve YA y si el servidor
   * falla se revierte y se avisa. Es lo que hace que la app se sienta viva en
   * recepción, donde se marcan decenas de citas al día.
   */
  const cambiarEstado = async (cita, estado) => {
    const previas = citas;
    setCitas((cs) => cs.map((c) => (c.id === cita.id ? { ...c, estado } : c)));
    try {
      await axios.put(`${API}/appointments/${cita.id}/status`, null, { params: { estado } });
      const etiqueta = estadoDe(estado).label.toLowerCase();
      toast.success(`${cita.paciente_nombre || 'La cita'} · ${etiqueta}`);
    } catch (err) {
      setCitas(previas);
      toast.error(err.response?.data?.detail || 'No se pudo actualizar la cita');
    }
  };

  const resumen = useMemo(() => {
    const total = citasHoy.length;
    const cerradas = citasHoy.filter((c) => ['atendido', 'cancelada', 'no_asistio'].includes(c.estado)).length;
    const confirmadas = citasHoy.filter((c) => ['confirmada', 'en_sala', 'atendido'].includes(c.estado)).length;
    const sinConfirmar = citasHoy.filter((c) => c.estado === 'citado').length;
    const enSala = citasHoy.filter((c) => c.estado === 'en_sala').length;
    const pendientesHoy = citasHoy
      .filter((c) => !['atendido', 'cancelada', 'no_asistio'].includes(c.estado))
      .sort((a, b) => String(a.hora_inicio).localeCompare(String(b.hora_inicio)));
    const ingresosHoy = citasHoy.filter((c) => c.pagado).reduce((s, c) => s + (Number(c.costo) || 0), 0);
    const ocupacion = total ? Math.round(((total - sinConfirmar) / total) * 100) : 0;

    return {
      total, cerradas, confirmadas, sinConfirmar, enSala, pendientesHoy, ingresosHoy,
      proxima: pendientesHoy[0] || null,
      avance: total ? Math.round((cerradas / total) * 100) : 0,
      ocupacion,
      porHora: Array.from({ length: 12 }, (_, i) => {
        const h = String(i + 8).padStart(2, '0');
        return citasHoy.filter((c) => String(c.hora_inicio).startsWith(h)).length;
      }),
    };
  }, [citasHoy]);

  const getPatientForAppointment = (apt) => pacientes[apt?.paciente_id];

  return (
    <Layout>
      <div className="space-y-5" data-testid="admin-dashboard">
        {/* ── Encabezado ─────────────────────────────────────────────────── */}
        <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="font-display text-[26px] font-extrabold leading-tight tracking-tight md:text-[30px]">
              Hoy en la clínica
            </h1>
            <p className="mt-1 text-[13.5px] text-muted-foreground">
              <span className="first-letter:uppercase">{fechaLarga()}</span>
              {!cargando && ` · ${resumen.total} citas programadas · ${resumen.pendientesHoy.length} por resolver`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/calendar')} className="h-9">
              <CalendarDays className="mr-1.5 h-4 w-4" /> Calendario
            </Button>
            <Button
              className="h-9 gap-1.5 bg-brand-gradient text-white shadow-[var(--shadow-glow)] hover:opacity-95"
              onClick={() => navigate('/calendar', { state: { accion: 'nueva-cita' } })}
              data-testid="go-to-calendar"
            >
              <Plus className="h-4 w-4" /> Nueva cita
            </Button>
          </div>
        </header>

        {/* ── Bento: resumen del día + KPIs ──────────────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-12">
          <section className="relative overflow-hidden rounded-xl border border-border/70 bg-brand-soft p-4 shadow-sm lg:col-span-4">
            <div className="absolute inset-0 bg-dots opacity-40" aria-hidden="true" />
            <div className="relative flex h-full flex-col">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-[15px] font-bold tracking-tight">El día de hoy</h2>
                <span className="flex items-center gap-1.5 rounded-full bg-card/80 px-2 py-0.5 text-[11px] font-semibold text-success ring-1 ring-inset ring-success/20">
                  <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse-soft" /> en vivo
                </span>
              </div>

              {cargando ? (
                <div className="mt-4 space-y-3">
                  <div className="skeleton h-8 w-28" />
                  <div className="skeleton h-2 w-full" />
                  <div className="skeleton h-12 w-full" />
                </div>
              ) : (
                <>
                  <div className="mt-3 flex items-end gap-2">
                    <span className="font-display text-[34px] font-extrabold leading-none tracking-tight tnum">
                      {resumen.total}
                    </span>
                    <span className="pb-1 text-[13px] text-muted-foreground">
                      citas · <span className="font-semibold text-foreground tnum">{resumen.enSala}</span> en sala
                    </span>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[11.5px] font-medium text-muted-foreground">
                      <span>Avance del día</span>
                      <span className="tnum">{resumen.avance}%</span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-card/70">
                      <div
                        className="h-full rounded-full bg-brand-gradient transition-[width] duration-500 ease-smooth"
                        style={{ width: `${resumen.avance}%` }}
                      />
                    </div>
                  </div>

                  {/* Carga por hora: se entiende la forma del día de un vistazo */}
                  <div className="mt-4 flex items-end gap-1" aria-hidden="true">
                    {resumen.porHora.map((n, i) => (
                      <Tooltip key={i}>
                        <TooltipTrigger asChild>
                          <span
                            className={cn(
                              'flex-1 rounded-sm transition-all',
                              n > 0 ? 'bg-primary/60' : 'bg-card/60',
                            )}
                            style={{ height: `${Math.max(4, n * 10)}px` }}
                          />
                        </TooltipTrigger>
                        <TooltipContent>{`${String(i + 8).padStart(2, '0')}:00 · ${n} ${n === 1 ? 'cita' : 'citas'}`}</TooltipContent>
                      </Tooltip>
                    ))}
                  </div>
                  <div className="mt-1 flex justify-between text-[10px] text-muted-foreground tnum">
                    <span>08h</span><span>13h</span><span>19h</span>
                  </div>

                  {resumen.proxima ? (
                    <button
                      type="button"
                      onClick={() => setSeleccion(resumen.proxima)}
                      className="mt-4 flex w-full items-center gap-2.5 rounded-lg border border-border/70 bg-card/90 px-3 py-2.5 text-left transition-colors hover:border-primary/40"
                    >
                      <Clock className="h-4 w-4 shrink-0 text-primary" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Siguiente
                        </span>
                        <span className="block truncate text-[13px] font-semibold">
                          {resumen.proxima.hora_inicio} · {resumen.proxima.paciente_nombre}
                        </span>
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  ) : (
                    <div className="mt-4 flex items-center gap-2.5 rounded-lg border border-border/70 bg-card/90 px-3 py-2.5">
                      <CheckCircle2 className="h-4 w-4 text-success" />
                      <span className="text-[13px] font-medium">No queda nada pendiente hoy</span>
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between text-[11.5px] text-muted-foreground">
                    <span>Cobrado hoy</span>
                    <span className="font-semibold text-foreground tnum">{dinero(resumen.ingresosHoy)}</span>
                  </div>
                </>
              )}
            </div>
          </section>

          <div className="grid grid-cols-2 gap-4 lg:col-span-8 xl:grid-cols-4">
            <KPICard
              title="Pacientes hoy"
              value={cargando ? '—' : resumen.total}
              icon={Users}
              tono="primary"
              cargando={cargando}
              subtitle={cargando ? undefined : `${resumen.confirmadas} confirmadas · ${resumen.sinConfirmar} por confirmar`}
              serie={serieSemanal(citas, ultimos7, () => true)}
            />
            <KPICard
              title="Ingresos del mes"
              value={cargando ? '—' : dinero(kpis?.ingresos_mes ?? 0)}
              icon={DollarSign}
              tono="success"
              cargando={cargando}
              subtitle="según citas atendidas"
              serie={serieSemanal(citas, ultimos7, (c) => c.estado === 'atendido' && c.pagado)}
            />
            <KPICard
              title="Citas atendidas"
              value={cargando ? '—' : (kpis?.citas_completadas ?? 0)}
              icon={CalendarCheck}
              tono="info"
              cargando={cargando}
              subtitle="este mes"
              serie={serieSemanal(citas, ultimos7, (c) => c.estado === 'atendido')}
            />
            <KPICard
              title="Canceladas / faltas"
              value={cargando ? '—' : (kpis?.citas_canceladas ?? 0)}
              icon={CalendarX}
              tono="destructive"
              cargando={cargando}
              subtitle="este mes · recuperables por WhatsApp"
              serie={serieSemanal(citas, ultimos7, (c) => ['cancelada', 'no_asistio'].includes(c.estado))}
            />
          </div>
        </div>

        {/* ── Agenda + pendientes ────────────────────────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-12">
          <section className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm lg:col-span-8">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border/70 px-4 py-3">
              <div>
                <h2 className="font-display text-[15px] font-bold tracking-tight">Agenda de hoy</h2>
                <p className="text-[11.5px] text-muted-foreground tnum">
                  {cargando ? 'Cargando…' : `${resumen.total} citas · ${resumen.cerradas} cerradas · ${resumen.pendientesHoy.length} por resolver`}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="h-8 gap-1 text-[12.5px]" onClick={() => navigate('/calendar')}>
                Ver calendario completo <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </header>

            <AgendaDelDia
              citas={citasHoy}
              pacientes={pacientes}
              cargando={cargando}
              onSelect={setSeleccion}
              onEstado={cambiarEstado}
            />
          </section>

          <div className="space-y-4 lg:col-span-4">
            {/* Cola de acciones: cada pendiente se resuelve en un clic */}
            <section className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm">
              <header className="px-4 pb-2 pt-4">
                <h2 className="font-display text-[15px] font-bold tracking-tight">Por resolver</h2>
                <p className="text-[11.5px] text-muted-foreground">Lo que se atora si nadie lo toca</p>
              </header>
              <div className="divide-y divide-border/60">
                <Pendiente
                  icon={Clock}
                  tono={pendientes.sinConfirmar > 0 ? 'warning' : 'success'}
                  titulo={`${pendientes.sinConfirmar} ${pendientes.sinConfirmar === 1 ? 'cita' : 'citas'} sin confirmar`}
                  texto="Próximos 7 días · recordatorio por WhatsApp"
                  onClick={() => navigate('/calendar')}
                  cargando={cargando}
                />
                <Pendiente
                  icon={UserRoundSearch}
                  tono={(pendientes.prospectosNuevos ?? 0) > 0 ? 'info' : 'success'}
                  titulo={`${pendientes.prospectosNuevos ?? 0} prospectos sin seguimiento`}
                  texto={(pendientes.prospectosNuevos ?? 0) === 0
                    ? 'Todos los prospectos ya tienen seguimiento'
                    : 'Nadie los ha contactado todavía'}
                  onClick={() => navigate('/prospectos')}
                  cargando={cargando}
                />
                <Pendiente
                  icon={Flame}
                  tono="primary"
                  titulo={`${pendientes.reto21 ?? 0} pacientes en reto de 21 días`}
                  texto="Revisa hoy quién no registró sus comidas"
                  onClick={() => navigate('/reto21')}
                  cargando={cargando}
                />
              </div>
            </section>

            <DoctorWidget doctors={doctores} citasHoy={citasHoy} cargando={cargando} />
          </div>
        </div>
      </div>

      {/* ── Detalle de la cita ─────────────────────────────────────────── */}
      <Sheet open={!!seleccion} onOpenChange={(v) => !v && setSeleccion(null)}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="pr-6">
              {seleccion?.paciente_nombre || 'Cita'}
            </SheetTitle>
          </SheetHeader>

          {seleccion && (() => {
            const paciente = getPatientForAppointment(seleccion);
            const estado = estadoDe(seleccion.estado);
            return (
              <div className="mt-6 space-y-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ring-1 ring-inset', estado.pill)}>
                    <span className={cn('h-1.5 w-1.5 rounded-full', estado.punto)} />
                    {estado.label}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[12px] font-medium text-muted-foreground tnum">
                    <Clock className="h-3.5 w-3.5" /> {seleccion.hora_inicio} – {seleccion.hora_fin}
                  </span>
                  {seleccion.pagado ? (
                    <span className="rounded-full bg-success/10 px-2.5 py-1 text-[12px] font-semibold text-success tnum">
                      Pagado {seleccion.costo ? dinero(seleccion.costo) : ''}
                    </span>
                  ) : (
                    <span className="rounded-full bg-warning/15 px-2.5 py-1 text-[12px] font-semibold text-warning">
                      Sin cobrar
                    </span>
                  )}
                </div>

                <div className="space-y-3 rounded-xl border border-border/70 bg-surface/50 p-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Motivo</p>
                    <p className="text-[13.5px] font-medium">{seleccion.motivo}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: seleccion.doctor_color }} />
                    <span className="text-[13px]">{seleccion.doctor_nombre}</span>
                  </div>
                  {paciente?.telefono && (
                    <div className="text-[13px] text-muted-foreground tnum">{paciente.telefono}</div>
                  )}
                </div>

                {paciente?.alertas_medicas?.length > 0 && (
                  <div className="rounded-xl border border-rose-500/25 bg-rose-500/[0.07] p-3.5">
                    <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-rose-600 dark:text-rose-300">
                      <AlertTriangle className="h-4 w-4" /> Alertas médicas
                    </div>
                    <ul className="space-y-1">
                      {paciente.alertas_medicas.map((a, i) => (
                        <li key={i} className="text-[13px] text-rose-700 dark:text-rose-200">· {a.descripcion}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-2">
                  {!['atendido', 'cancelada', 'no_asistio'].includes(seleccion.estado) && (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => { cambiarEstado(seleccion, 'en_sala'); setSeleccion(null); }}
                      >
                        <DoorOpen className="h-4 w-4" /> En sala
                      </Button>
                      <Button
                        className="gap-1.5 bg-success text-white hover:bg-success/90"
                        onClick={() => { cambiarEstado(seleccion, 'atendido'); setSeleccion(null); }}
                      >
                        <CheckCircle2 className="h-4 w-4" /> Atendido
                      </Button>
                    </div>
                  )}
                  <Button
                    className="w-full"
                    onClick={() => {
                      const id = seleccion.paciente_id || seleccion.id;
                      setSeleccion(null);
                      navigate(`/patients/${id}`);
                    }}
                    data-testid="view-patient-btn"
                  >
                    Ver expediente completo
                  </Button>
                </div>
              </div>
            );
          })()}
        </SheetContent>
      </Sheet>
    </Layout>
  );
};

/** Fila de la cola de pendientes: icono con tono, título, texto y flecha. */
const Pendiente = ({ icon: Icon, titulo, texto, tono = 'primary', onClick, cargando }) => {
  const tonos = {
    warning: 'bg-warning/15 text-warning',
    info: 'bg-info/10 text-info',
    success: 'bg-success/10 text-success',
    primary: 'bg-primary/[0.09] text-primary',
  };

  if (cargando) {
    return (
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="skeleton h-8 w-8 rounded-lg" />
        <div className="flex-1 space-y-2">
          <div className="skeleton h-3 w-36" />
          <div className="skeleton h-2.5 w-24" />
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
    >
      <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-lg', tonos[tono])}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold">{titulo}</span>
        <span className="block truncate text-[11.5px] text-muted-foreground">{texto}</span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
};

export default AdminDashboard;
